import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Users,
  Search,
  Heart,
  Briefcase,
  HandCoins,
  CircleHelp,
  Building2,
  Star,
} from "lucide-react";
import { api, type Person, type PeopleSort } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

const TABS = [
  { id: "people", label: "People", Icon: Users },
  { id: "orgs", label: "Orgs", Icon: Building2 },
] as const;

type TabId = (typeof TABS)[number]["id"];

interface CategoryFilter {
  id: string;
  label: string;
  rel: string | null;
  Icon: typeof Users;
  classes: string;
}

const CATEGORIES: CategoryFilter[] = [
  { id: "all", label: "Everyone", rel: null, Icon: Users, classes: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200" },
  { id: "family", label: "Family", rel: "family", Icon: Heart, classes: "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300" },
  { id: "friend", label: "Friends", rel: "friend", Icon: Star, classes: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" },
  { id: "colleague", label: "Colleagues", rel: "colleague", Icon: Briefcase, classes: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300" },
  { id: "commercial", label: "Commercial", rel: "commercial_contact", Icon: HandCoins, classes: "bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300" },
  { id: "unclassified", label: "Unclassified", rel: "unclassified", Icon: CircleHelp, classes: "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300" },
];

function categoryFor(person: Person): { id: string; label: string; classes: string } {
  const rel = person.relationship?.type || person.relationship?.inferred_subtype || "unclassified";
  const lower = rel.toLowerCase();
  if (lower.includes("commercial") || ["dealer", "vendor", "broker", "agent", "merchant"].includes(lower)) {
    const sub =
      (person.relationship?.inferred_subtype || person.relationship?.type || "").toLowerCase();
    const niceSub = sub && sub !== "commercial_contact" ? sub.replaceAll("_", " ") : "commercial";
    return { id: "commercial", label: niceSub, classes: "bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300" };
  }
  if (["wife", "husband", "spouse", "partner", "mother", "father", "sister", "brother", "son", "daughter", "cousin", "aunt", "uncle", "nephew", "niece", "family"].includes(lower)) {
    return { id: "family", label: lower, classes: "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300" };
  }
  if (["friend", "close_friend", "acquaintance"].includes(lower)) {
    return { id: "friend", label: lower.replaceAll("_", " "), classes: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" };
  }
  if (["colleague", "coworker", "report", "manager", "supervisor", "subordinate", "boss", "client"].includes(lower)) {
    return { id: "colleague", label: lower, classes: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300" };
  }
  if (lower === "unclassified" || !lower) {
    return { id: "unclassified", label: "unclassified", classes: "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300" };
  }
  return { id: "other", label: lower, classes: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200" };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function People() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabId = (searchParams.get("tab") || "people") as TabId;
  const catId = searchParams.get("cat") || "all";
  const sort = (searchParams.get("sort") || "strength") as PeopleSort;

  const activeCat = CATEGORIES.find((c) => c.id === catId) || CATEGORIES[0];

  const [search, setSearch] = useState("");
  const [people, setPeople] = useState<Person[]>([]);
  const [orgs, setOrgs] = useState<Array<{ id: string; name: string; sub_kind: string | null; confirmed?: boolean; last_seen?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    if (tabId === "people") {
      api
        .peopleList({
          sort,
          search: search || undefined,
          relationship: activeCat.rel || undefined,
          limit: 120,
        })
        .then((r) => setPeople(r.people || []))
        .catch((e: Error) => setError(e.message))
        .finally(() => setLoading(false));
    } else {
      fetch("/api/orgs", { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((j) => setOrgs((j.orgs || []) as typeof orgs))
        .catch((e: Error) => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [tabId, sort, search, activeCat.rel]);

  useEffect(refresh, [refresh]);

  const setTab = useCallback(
    (next: TabId) => {
      const sp = new URLSearchParams(searchParams);
      if (next === "people") sp.delete("tab");
      else sp.set("tab", next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setCat = useCallback(
    (next: string) => {
      const sp = new URLSearchParams(searchParams);
      if (next === "all") sp.delete("cat");
      else sp.set("cat", next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "People" }]} />

      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            People &amp; Orgs
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Who matters, in what way, and how you connect.
          </p>
        </div>
      </header>

      <TeachingBanner
        id="people-intro"
        title="What is this?"
        body="People is your dossier surface. Each tile shows a category
              chip (family, friend, colleague, commercial contact,
              unclassified). A dashed border means Memso inferred the
              category; tap a tile to confirm or correct it. Sort by
              strength to see who you connect with most."
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <nav aria-label="People tab" className="flex gap-1.5">
          {TABS.map((t) => {
            const active = t.id === tabId;
            const Icon = t.Icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition " +
                  (active
                    ? "bg-indigo-500 text-white shadow ring-2 ring-indigo-300/40"
                    : "bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 hover:bg-slate-200")
                }
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
        {tabId === "people" && (
          <div className="flex items-center gap-2">
            <label className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email..."
                className="rounded-lg pl-8 pr-3 py-1.5 text-sm bg-white dark:bg-slate-900/60 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
              />
            </label>
            <select
              value={sort}
              onChange={(e) => {
                const sp = new URLSearchParams(searchParams);
                if (e.target.value === "strength") sp.delete("sort");
                else sp.set("sort", e.target.value);
                setSearchParams(sp, { replace: true });
              }}
              className="rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-slate-900/60 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
              aria-label="Sort"
            >
              <option value="strength">Strength</option>
              <option value="recent">Recent</option>
              <option value="alpha">A-Z</option>
              <option value="frequent">Most-mailed</option>
            </select>
          </div>
        )}
      </div>

      {tabId === "people" && (
        <nav aria-label="Category filters" className="-mx-1 flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => {
            const Icon = c.Icon;
            const active = c.id === catId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                className={
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition " +
                  (active
                    ? "bg-indigo-500 text-white shadow ring-2 ring-indigo-300/40"
                    : "bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 hover:bg-slate-200")
                }
              >
                <Icon className="w-4 h-4" />
                {c.label}
              </button>
            );
          })}
        </nav>
      )}

      {error && (
        <div className="rounded-lg border border-rose-300/60 bg-rose-50/60 dark:border-rose-700/40 dark:bg-rose-900/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {tabId === "people" ? (
        <PeopleGrid people={people} loading={loading} />
      ) : (
        <OrgsGrid orgs={orgs} loading={loading} />
      )}
    </div>
  );
}

function PeopleGrid({ people, loading }: { people: Person[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="text-sm text-slate-500 dark:text-slate-400">Loading...</div>
    );
  }
  if (people.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/40 bg-white/60 dark:bg-slate-900/40 p-6 text-center space-y-2">
        <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
          No people match this filter yet.
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
          Memso builds People from your mail, calendar, and Teams
          conversations. Confirmed relationships have a solid border;
          inferred ones are dashed and one-tap confirmable.
        </p>
      </div>
    );
  }
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {people.map((p) => (
        <li key={p.id}>
          <PersonTile person={p} />
        </li>
      ))}
    </ul>
  );
}

function PersonTile({ person }: { person: Person }) {
  const cat = categoryFor(person);
  const inferred = person.relationship ? !person.relationship.confirmed : true;
  const employer = person.inferred_employer || person.organization || null;
  const role = person.inferred_role || person.role || null;
  return (
    <Link
      to={`/people/${encodeURIComponent(person.id)}`}
      className={
        "block rounded-2xl bg-white/70 dark:bg-slate-900/50 p-4 transition hover:bg-white dark:hover:bg-slate-800 hover:shadow-md " +
        (inferred
          ? "border-2 border-dashed border-amber-400/50 dark:border-amber-500/30"
          : "border border-slate-200/70 dark:border-slate-700/40")
      }
    >
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          {person.avatar_url ? (
            <img
              src={person.avatar_url}
              alt=""
              className={
                "w-12 h-12 rounded-full object-cover " +
                (inferred ? "ring-2 ring-amber-400/60 ring-offset-1 ring-offset-white dark:ring-offset-slate-900" : "")
              }
            />
          ) : (
            <div
              className={
                "w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm bg-gradient-to-br from-indigo-500 to-sky-500 text-white " +
                (inferred ? "ring-2 ring-amber-400/60 ring-offset-1 ring-offset-white dark:ring-offset-slate-900" : "")
              }
            >
              {initials(person.display_name) || "?"}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
            {person.display_name}
          </p>
          {(role || employer) && (
            <p className="text-xs text-slate-600 dark:text-slate-300 truncate">
              {role}
              {role && employer && " · "}
              {employer}
            </p>
          )}
          {person.primary_email && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{person.primary_email}</p>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cat.classes}`}>
          {cat.label}
        </span>
        {typeof person.interactionCount === "number" && person.interactionCount > 0 && (
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            {person.interactionCount} touch{person.interactionCount === 1 ? "" : "es"}
          </span>
        )}
      </div>
    </Link>
  );
}

function OrgsGrid({
  orgs,
  loading,
}: {
  orgs: Array<{ id: string; name: string; sub_kind: string | null; confirmed?: boolean; last_seen?: string }>;
  loading: boolean;
}) {
  if (loading) {
    return <div className="text-sm text-slate-500 dark:text-slate-400">Loading...</div>;
  }
  if (orgs.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/40 bg-white/60 dark:bg-slate-900/40 p-6 text-center space-y-2">
        <p className="text-base font-semibold text-slate-900 dark:text-slate-100">No orgs found.</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
          Orgs accrete from mail signatures, calendar invites, and confirmed
          relationships. Confirm a person's employer to seed one.
        </p>
      </div>
    );
  }
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {orgs.map((o) => (
        <li key={o.id}>
          <Link
            to={`/orgs/${encodeURIComponent(o.id)}`}
            className={
              "block rounded-2xl p-4 transition hover:bg-white dark:hover:bg-slate-800 hover:shadow-md bg-white/70 dark:bg-slate-900/50 " +
              (o.confirmed === false
                ? "border-2 border-dashed border-amber-400/50 dark:border-amber-500/30"
                : "border border-slate-200/70 dark:border-slate-700/40")
            }
          >
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-slate-600 to-slate-800 text-white">
                <Building2 className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{o.name}</p>
                {o.sub_kind && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{o.sub_kind}</p>
                )}
                {o.confirmed === false && (
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                    Inferred
                  </span>
                )}
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
